import {
  Divider,
  Input,
  makeStyles,
  mergeClasses,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { Item } from "../common/Item";
import { useContext, useEffect, useRef, useState } from "react";
import { DataContext } from "../../context/DataContext";
import { ApiContext } from "../../Root";
import { useAuth } from "../../authentication/AuthContextProvider";

const useStyles = makeStyles({
  divider: {
    alignSelf: "center",
    marginTop: tokens.spacingVerticalM,
    // marginBlock: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorBrandBackground,
  },
  itemWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingHorizontalXL,
  },
  loader: {
    marginTop: tokens.spacingVerticalL,
  },
  bottomLoader: {
    marginTop: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalL,
  },
  ordersWrapper: {
    marginTop: tokens.spacingVerticalM
  }
});

export const Orders = (): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  const dataContext = useContext(DataContext);
  const apiContext = useContext(ApiContext);
  const auth = useAuth();
  const { orders, isLoading, isFetchingMore, nextPageToken, selectedCustomer } =
    dataContext.state;

  const [searchInput, setSearchInput] = useState("");
  const sentinelRef = useRef<HTMLDivElement>(null);

  const searchInputRef = useRef(searchInput);
  searchInputRef.current = searchInput;
  const isFirstSearch = useRef(true);

  useEffect(() => {
    setSearchInput("");
    isFirstSearch.current = true;
  }, [selectedCustomer?.id]);

  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (!selectedCustomer || !auth.user) return;
      dataContext.setRootState((prev) => ({
        ...prev,
        orders: [],
        nextPageToken: null,
      }));
      apiContext.customerOrderApi.getOrders(
        dataContext,
        auth.user,
        selectedCustomer,
        t,
        undefined,
        searchInputRef.current || undefined,
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          nextPageToken &&
          !isFetchingMore &&
          !isLoading
        ) {
          if (!selectedCustomer || !auth.user) return;
          apiContext.customerOrderApi.getOrders(
            dataContext,
            auth.user,
            selectedCustomer,
            t,
            nextPageToken,
            searchInputRef.current || undefined,
          );
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.unobserve(el);
  }, [nextPageToken, isFetchingMore, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className={styles.itemWrapper}>
        <Input
          appearance="outline"
          placeholder="Sök..."
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>
      <Divider appearance="brand" className={styles.divider} />
      <div className={mergeClasses(styles.itemWrapper, styles.ordersWrapper)}>
        {isLoading ? (
          <Spinner className={styles.loader} size="large" />
        ) : orders?.length > 0 ? (
          orders.map((order, i) => <Item order={order} key={i} />)
        ) : null}
      </div>
      <div ref={sentinelRef} />
      {isFetchingMore && (
        <Spinner
          className={styles.bottomLoader}
          size="medium"
          label={t("loadingMore")}
        />
      )}
    </>
  );
};
